import { cn } from "@/lib/utils";
import { CircleDollarSign, FileSearch2, Radar, Truck } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

type FlowStepId = "quote" | "retrieve" | "book" | "track";

const STEPS: Array<{
  id: FlowStepId;
  label: string;
  description: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  {
    id: "quote",
    label: "Get Quote",
    description: "Build lane + compare rates",
    path: "/quotes",
    icon: CircleDollarSign,
  },
  {
    id: "retrieve",
    label: "Retrieve",
    description: "Open existing reference",
    path: "/quotes/retrieve",
    icon: FileSearch2,
  },
  {
    id: "book",
    label: "Book",
    description: "Turn selected rate into shipment",
    path: "/shipments/book",
    icon: Truck,
  },
  {
    id: "track",
    label: "Track",
    description: "Follow carrier events live",
    path: "/tracking",
    icon: Radar,
  },
];

export function WorkflowRail({ current, className }: { current: FlowStepId; className?: string }) {
  const navigate = useNavigate();

  return (
    <section className={cn("rounded-lg border-[0.5px] border-border bg-card p-3", className)}>
      <div className="mb-2 px-1">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground font-mono">Workflow</p>
      </div>
      <div className="grid gap-2 md:grid-cols-4">
        {STEPS.map((step) => {
          const active = step.id === current;
          const Icon = step.icon;
          return (
            <button
              key={step.id}
              type="button"
              className={cn(
                "flex items-start gap-2 rounded-lg border-[0.5px] px-3 py-2 text-left transition-colors",
                active
                  ? "border-foreground/20 bg-accent text-foreground"
                  : "border-border bg-secondary/50 text-muted-foreground hover:bg-accent hover:text-foreground",
              )}
              onClick={() => navigate(step.path)}
            >
              <span
                className={cn(
                  "mt-0.5 grid size-7 shrink-0 place-items-center rounded-lg",
                  active ? "bg-foreground/10 text-foreground" : "bg-muted text-muted-foreground",
                )}
              >
                <Icon className="size-4" />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{step.label}</span>
                <span className="block truncate text-xs text-muted-foreground">{step.description}</span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
